import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import APIs from "../../Constants/apis.js";
import CustomModal from "../../components/CustomComponents/modal/customModal.jsx";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import { logout } from "../../app/auth/authSlice.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import "../compnay/ManageCompany.css";

const defaultForm = {
  slab_ID: 0,
  minKm: "0",
  maxKm: "",
  feeAmount: "",
  isActive: true,
  sortOrder: "0",
};

const ManageDeliveryFeeSlabs = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const [slabs, setSlabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(defaultForm);

  const fetchSlabs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_MARKETPLACE_DELIVERY_FEE_SLABS);
      if (response.data.status) {
        setSlabs(Array.isArray(response.data.data) ? response.data.data : []);
      } else {
        showErrorToast(response.data.message || "Failed to load delivery fee slabs");
      }
    } catch (err) {
      if (err.response?.status === 401) {
        dispatch(logout());
        setTimeout(() => navigate("/user/login", { replace: true }), 1000);
      } else {
        showErrorToast(err.response?.data?.message || "Failed to load delivery fee slabs");
      }
    } finally {
      setLoading(false);
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    fetchSlabs();
  }, [fetchSlabs]);

  const openCreate = () => {
    setForm({ ...defaultForm });
    setShowModal(true);
  };

  const openEdit = (slab) => {
    setForm({
      slab_ID: slab.slabId || slab.Slab_ID || 0,
      minKm: String(slab.minKm ?? slab.MinKm ?? "0"),
      maxKm:
        slab.maxKm == null && slab.MaxKm == null
          ? ""
          : String(slab.maxKm ?? slab.MaxKm ?? ""),
      feeAmount: String(slab.feeAmount ?? slab.FeeAmount ?? ""),
      isActive: slab.isActive ?? slab.IsActive ?? true,
      sortOrder: String(slab.sortOrder ?? slab.SortOrder ?? "0"),
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    const minKm = parseFloat(form.minKm);
    const feeAmount = parseFloat(form.feeAmount);
    const sortOrder = parseInt(form.sortOrder, 10) || 0;
    const maxKmRaw = (form.maxKm || "").trim();
    const maxKm = maxKmRaw === "" ? null : parseFloat(maxKmRaw);

    if (Number.isNaN(minKm) || minKm < 0) {
      showErrorToast("Min km must be 0 or greater");
      return;
    }
    if (maxKm != null && (Number.isNaN(maxKm) || maxKm <= minKm)) {
      showErrorToast("Max km must be greater than Min km (leave blank for open-ended)");
      return;
    }
    if (Number.isNaN(feeAmount) || feeAmount < 0) {
      showErrorToast("Fee amount must be 0 or greater");
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_UPSERT_MARKETPLACE_DELIVERY_FEE_SLAB, {
        slab_ID: form.slab_ID,
        minKm,
        maxKm,
        feeAmount,
        isActive: form.isActive,
        sortOrder,
      });
      if (response.data.status) {
        showSuccessToast(form.slab_ID > 0 ? "Slab updated" : "Slab created");
        setShowModal(false);
        fetchSlabs();
      } else {
        showErrorToast(response.data.message || "Failed to save slab");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to save slab");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (slab) => {
    const slabId = slab.slabId || slab.Slab_ID;
    const nextActive = !(slab.isActive ?? slab.IsActive);
    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.POST_SET_MARKETPLACE_DELIVERY_FEE_SLAB_ACTIVE, {
        slab_ID: slabId,
        isActive: nextActive,
      });
      if (response.data.status) {
        showSuccessToast(nextActive ? "Slab activated" : "Slab deactivated");
        fetchSlabs();
      } else {
        showErrorToast(response.data.message || "Failed to update slab");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to update slab");
    } finally {
      setLoading(false);
    }
  };

  const formatRange = (slab) => {
    const min = slab.minKm ?? slab.MinKm ?? 0;
    const max = slab.maxKm ?? slab.MaxKm;
    if (max == null) return `${min}+ km`;
    return `${min} – ${max} km`;
  };

  const activeSlabCount = slabs.filter((s) => s.isActive ?? s.IsActive).length;

  const actionBtnStyle = (color) => ({
    width: "32px",
    height: "32px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "6px",
    border: `1px solid ${color}`,
    color,
    padding: 0,
  });

  return (
    <div>
      {loading && <Preloader />}

      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="#/">Home</a></li>
            <li className="breadcrumb-item active">Delivery Fee Slabs</li>
          </ol>
          <h1 className="page-header mb-0">Go Fatafat Delivery Fee Slabs</h1>
        </div>
        <div className="ms-auto">
          <button
            type="button"
            className="btn px-4"
            onClick={openCreate}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
              boxShadow: `0 2px 8px ${themeColor}40`,
            }}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-white"></i>
            Add Slab
          </button>
        </div>
      </div>

      <div className="card border-0">
        <div className="tab-content p-3">
          <div className="mb-4">
            <div
              className="card filter-card"
              style={{
                position: "relative",
                zIndex: 1,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                borderRadius: "12px",
                border: "none",
                boxShadow: `0 4px 20px ${themeColor}40`,
              }}
            >
              <div className="card-body p-3">
                <div className="row g-3 align-items-center">
                  <div className="col-md-8">
                    <div className="d-flex align-items-start gap-3">
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: "10px",
                          background: "rgba(255,255,255,0.2)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <i className="fa fa-route text-white" />
                      </div>
                      <div>
                        <div className="text-white fw-semibold mb-1">Global km-based delivery pricing</div>
                        <div className="text-white-50 small mb-0">
                          Customer fee equals freelancer pay. Use half-open bands: Min km inclusive, Max km exclusive.
                          Leave Max km blank on the last band for open-ended distance (e.g. 10+ km).
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div
                      className="d-flex justify-content-md-end gap-3 text-white"
                      style={{ fontSize: "13px" }}
                    >
                      <div className="text-center">
                        <div className="fw-bold fs-5">{slabs.length}</div>
                        <div className="text-white-50">Total slabs</div>
                      </div>
                      <div className="text-center">
                        <div className="fw-bold fs-5">{activeSlabCount}</div>
                        <div className="text-white-50">Active</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className="table-responsive table-container"
            style={{
              minHeight: "200px",
              maxHeight: "450px",
              overflowY: "auto",
              overflowX: "auto",
              position: "relative",
              zIndex: 1,
            }}
          >
            <table className="table table-hover align-middle mb-0">
              <thead
                className="sticky-header"
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                  color: isDarkMode ? "#ffffff" : "#000000",
                }}
              >
                <tr>
                  <th className="pt-2 pb-2">Range</th>
                  <th className="pt-2 pb-2 text-center">Min km</th>
                  <th className="pt-2 pb-2 text-center">Max km</th>
                  <th className="pt-2 pb-2 text-end">Fee (Rs)</th>
                  <th className="pt-2 pb-2 text-center">Sort</th>
                  <th className="pt-2 pb-2 text-center">Active</th>
                  <th className="pt-2 pb-2 text-center" style={{ width: 120 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {slabs.length > 0 ? (
                  slabs.map((slab) => {
                    const id = slab.slabId || slab.Slab_ID;
                    const active = slab.isActive ?? slab.IsActive;
                    const fee = slab.feeAmount ?? slab.FeeAmount;
                    return (
                      <tr key={id}>
                        <td className="fw-semibold">{formatRange(slab)}</td>
                        <td className="text-center">{slab.minKm ?? slab.MinKm}</td>
                        <td className="text-center">{slab.maxKm ?? slab.MaxKm ?? "∞"}</td>
                        <td className="text-end fw-semibold">{Number(fee).toLocaleString()}</td>
                        <td className="text-center">{slab.sortOrder ?? slab.SortOrder ?? 0}</td>
                        <td className="text-center">
                          <span className={`badge ${active ? "bg-success" : "bg-secondary"}`}>
                            {active ? "Yes" : "No"}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="d-inline-flex justify-content-center gap-1">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => openEdit(slab)}
                              title="Edit"
                              style={actionBtnStyle(themeColor)}
                            >
                              <i className="fa fa-edit" style={{ fontSize: "12px" }} />
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={() => handleToggleActive(slab)}
                              title={active ? "Deactivate" : "Activate"}
                              style={actionBtnStyle(active ? "#ffc107" : "#28a745")}
                            >
                              <i className={`fa ${active ? "fa-ban" : "fa-check"}`} style={{ fontSize: "12px" }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center text-muted py-4">
                      No slabs configured. Add bands starting from 0 km with one open-ended last band.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <span className="text-muted small">{slabs.length} slab(s)</span>
            <span className="text-muted small">
              Example: 0–3 km → Rs 80, 3–5 km → Rs 120, 10+ km → Rs 250
            </span>
          </div>
        </div>
      </div>

      <CustomModal
        title={form.slab_ID > 0 ? "Edit Delivery Fee Slab" : "Add Delivery Fee Slab"}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleSave}
        showButton={true}
        submitButtonText={form.slab_ID > 0 ? "Update slab" : "Create slab"}
        isCentered
        modalSize="modal-lg"
        maxHeight="85vh"
        zIndex={10050}
      >
        <div className="row g-3">
          <div className="col-12">
            <div
              className="small text-muted p-3 mb-1"
              style={{
                borderRadius: "10px",
                background: isDarkMode ? "rgba(45, 52, 54, 0.45)" : "#f8f9fa",
                border: isDarkMode ? "1px solid rgba(255,255,255,0.08)" : "1px solid #eceff1",
              }}
            >
              Distance is calculated at checkout from restaurant to customer. Overlapping active bands are not allowed.
            </div>
          </div>

          <div className="col-md-6">
            <FormInput
              label="Min km (inclusive)"
              type="number"
              value={form.minKm}
              onchange={(e) => setForm((f) => ({ ...f, minKm: e.target.value }))}
              placeHolder="0"
            />
          </div>

          <div className="col-md-6">
            <FormInput
              label="Max km (exclusive, blank = open-ended)"
              type="number"
              value={form.maxKm}
              onchange={(e) => setForm((f) => ({ ...f, maxKm: e.target.value }))}
              placeHolder="Leave blank for 10+ style band"
            />
          </div>

          <div className="col-md-6">
            <FormInput
              label="Fee amount (Rs)"
              type="number"
              value={form.feeAmount}
              onchange={(e) => setForm((f) => ({ ...f, feeAmount: e.target.value }))}
              placeHolder="e.g. 120"
            />
          </div>

          <div className="col-md-6">
            <FormInput
              label="Sort order"
              type="number"
              value={form.sortOrder}
              onchange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
              placeHolder="0"
            />
          </div>

          <div className="col-12 d-flex align-items-end pb-1">
            <ToggleSwitch
              isChecked={form.isActive}
              onToggle={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
              label="Active"
              name="isActive"
            />
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default ManageDeliveryFeeSlabs;
